import React, { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, Download, Info, Loader2, Minus, RefreshCw,
} from "lucide-react";
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import api from "../lib/api";
import { LoadErrorBox } from "../components/common/LoadErrorBox";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";

// Colori linee: palette Okabe-Ito, distinguibile anche con daltonismo.
// Il giallo brand #F4F430 è riservato alla zona selezionata, mai alle linee.
const ZONE_COLORS = {
  NORD: "#0072B2",
  CNOR: "#E69F00",
  CSUD: "#009E73",
  SUD: "#CC79A7",
  CALA: "#56B4E9",
  SICI: "#D55E00",
  SARD: "#16161A",
};
const DEFAULT_VISIBLE = { NORD: true, CNOR: true, CSUD: true, SUD: false, CALA: false, SICI: false, SARD: false };

// Tratteggio distinto per zona: quando due zone hanno lo stesso prezzo le
// linee si sovrappongono — i tratteggi diversi lasciano vedere entrambe.
const ZONE_DASH = {
  NORD: "",
  CNOR: "7 3",
  CSUD: "2 2",
  SUD: "9 3 2 3",
  CALA: "1 3",
  SICI: "12 4",
  SARD: "4 2",
};

const RANGE_OPTIONS = [
  { key: "today", label: "Oggi" },
  { key: "tomorrow", label: "Domani" },
  { key: "7d", label: "Ultimi 7 giorni" },
  { key: "30d", label: "Ultimi 30 giorni" },
];

const fmt2 = (v) =>
  v == null ? "—" : Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt1 = (v) =>
  v == null ? "—" : Number(v).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
};
const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

const Sparkline = ({ values, color }) => {
  if (!values || values.length < 2) {
    return <div className="h-8" />;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const w = 120;
  const h = 30;
  const pts = values
    .map((v, i) => `${((i / (values.length - 1)) * w).toFixed(1)},${(h - 2 - ((v - min) / span) * (h - 4)).toFixed(1)}`)
    .join(" ");
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
};

const ChartTooltip = ({ active, payload, label, zones }) => {
  if (!active || !payload || payload.length === 0) return null;
  const full = payload[0]?.payload?.full_label || label;
  const groups = new Map();
  payload.forEach((p) => {
    if (p.value == null) return;
    const k = Number(p.value).toFixed(2);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(p.dataKey);
  });
  const ordered = [...groups.entries()].sort((a, b) => Number(b[0]) - Number(a[0]));
  return (
    <div className="bg-white border border-border rounded-[10px] px-3 py-2 text-xs shadow-md max-w-xs" data-testid="energia-chart-tooltip">
      <div className="font-semibold text-text-primary mb-1">{full}</div>
      {ordered.map(([val, codes]) => (
        <div key={val} className="flex items-center gap-1.5 py-0.5">
          <span className="flex gap-0.5 shrink-0">
            {codes.map((c) => (
              <span key={c} className="w-2 h-2 rounded-full" style={{ background: ZONE_COLORS[c] }} />
            ))}
          </span>
          <span className="text-text-secondary min-w-0">
            {codes.map((c) => zones.find((z) => z.code === c)?.label || c).join(", ")}
            {codes.length > 1 ? " (sovrapposte)" : ""}
          </span>
          <span className="ml-auto pl-3 tabular-nums font-medium text-text-primary whitespace-nowrap">{fmt2(Number(val))} €/MWh</span>
        </div>
      ))}
    </div>
  );
};

const ZoneCard = ({ zone }) => {
  const delta = zone.delta_pct;
  const DeltaIcon = delta == null ? Minus : delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Minus;
  const deltaColor = delta == null ? "text-text-muted" : delta > 0 ? "text-red-600" : delta < 0 ? "text-emerald-700" : "text-text-muted";
  return (
    <div
      className="bg-surface border border-border rounded-[10px] px-3 py-3 min-w-0"
      data-testid={`zone-card-${zone.code}`}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="text-[11px] font-semibold text-text-secondary whitespace-nowrap">{zone.label}</span>
        <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium shrink-0 ${deltaColor}`} data-testid={`zone-delta-${zone.code}`}>
          <DeltaIcon size={12} />
          {delta == null ? "—" : `${delta > 0 ? "+" : ""}${fmt1(delta)}%`}
        </span>
      </div>
      <div
        className="mt-1.5 text-2xl leading-none text-text-primary"
        style={{ fontFamily: "var(--wl-font-display)" }}
        data-testid={`zone-avg-${zone.code}`}
      >
        {fmt2(zone.avg_today_eur_mwh)}
        <span className="text-[11px] text-text-muted font-normal ml-1" style={{ fontFamily: "var(--wl-font-body)" }}>€/MWh</span>
      </div>
      <div className="text-[11px] text-text-muted mt-0.5">{fmt2(zone.avg_today_cent_kwh)} c€/kWh</div>
      <div className="mt-1.5">
        <Sparkline values={zone.spark} color={ZONE_COLORS[zone.code]} />
      </div>
    </div>
  );
};

export default function EnergiaMercato() {
  const { user } = useAuth();
  const canRefresh = ["admin", "group_admin", "super_admin"].includes(user?.role);
  const [overview, setOverview] = useState(null);
  const [serie, setSerie] = useState(null);
  const [range, setRange] = useState("today");
  const [visible, setVisible] = useState(DEFAULT_VISIBLE);
  const [loading, setLoading] = useState(true);
  const [loadingSerie, setLoadingSerie] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [overviewFailed, setOverviewFailed] = useState(false);
  const [serieFailed, setSerieFailed] = useState(false);

  const loadOverview = useCallback(() => {
    return api.get("/energia/mercato/overview")
      .then(({ data }) => { setOverview(data); setForbidden(false); setOverviewFailed(false); })
      .catch((err) => {
        if (err?.response?.status === 403) setForbidden(true);
        else setOverviewFailed(true);
      });
  }, []);

  const loadSerie = useCallback((r) => {
    setLoadingSerie(true);
    return api.get(`/energia/mercato/serie?range=${r}`)
      .then(({ data }) => { setSerie(data); setSerieFailed(false); })
      .catch(() => setSerieFailed(true))
      .finally(() => setLoadingSerie(false));
  }, []);

  useEffect(() => {
    Promise.all([loadOverview(), loadSerie("today")]).finally(() => setLoading(false));
  }, [loadOverview, loadSerie]);

  const changeRange = (r) => {
    setRange(r);
    loadSerie(r);
  };

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/energia/mercato/refresh");
      if (data.ok) {
        toast.success(`Dati aggiornati: ${data.zones_updated} zone.`);
      } else {
        toast.info(data.error || "Nessun nuovo dato disponibile.");
      }
      await Promise.all([loadOverview(), loadSerie(range)]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Aggiornamento non riuscito.");
    } finally {
      setRefreshing(false);
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/energia/mercato/export?range=${range}`, { responseType: "blob" });
      const disp = res.headers?.["content-disposition"] || "";
      const match = disp.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `Prezzi_zonali_${range}.xlsx`;
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export scaricato.");
    } catch {
      toast.error("Nessun dato da esportare per l'intervallo scelto.");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6" data-testid="energia-mercato-loading">
        <Loader2 className="animate-spin text-text-muted" size={20} />
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="max-w-6xl mx-auto p-6" data-testid="energia-mercato-forbidden">
        <div className="bg-surface border border-border rounded-[10px] p-6 text-sm text-text-secondary">
          Questa sezione non è attiva per la tua organizzazione.
        </div>
      </div>
    );
  }

  const status = overview?.status || {};
  const notConfigured = overview && !overview.configured;
  const hasData = overview?.has_data;
  const linkDown = overview?.configured && ["error", "invalid_token"].includes(status.status);
  const zones = serie?.zones || [];
  const rows = serie?.rows || [];
  const averages = serie?.averages || {};
  // Etichette asse X a passo costante (mai "preserveStartEnd": spaziatura irregolare)
  const tickStep = rows.length <= 10
    ? 1
    : ([2, 3, 4, 5, 6, 10, 12, 15, 20].find((s) => rows.length / s <= 10) || Math.ceil(rows.length / 10));

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="energia-mercato-page">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary">Mercato</h1>
          <p className="text-sm text-text-muted mt-1">
            Prezzi all'ingrosso dell'energia elettrica per zona di mercato (day-ahead).
          </p>
        </div>
        {canRefresh && !notConfigured && (
          <Button
            onClick={doRefresh}
            disabled={refreshing}
            variant="outline"
            className="border-accent text-accent hover:bg-accent/10"
            data-testid="energia-refresh-btn"
          >
            {refreshing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <RefreshCw size={14} className="mr-1.5" />}
            Aggiorna adesso
          </Button>
        )}
      </div>

      {notConfigured ? (
        <div
          className="bg-surface border border-border rounded-[10px] p-8 text-center"
          data-testid="energia-not-configured"
        >
          <Info size={22} className="mx-auto text-text-muted mb-3" />
          <div className="text-sm text-text-secondary">
            Collegamento ai prezzi di mercato non configurato.
          </div>
        </div>
      ) : (
        <>
          {linkDown && hasData && (
            <div
              className="mb-4 flex items-start gap-2 rounded-[10px] border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900"
              data-testid="energia-stale-banner"
            >
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>
                Ultimo aggiornamento: <b>{fmtDateTime(status.last_success_at)}</b>.
                {" "}{status.status === "invalid_token"
                  ? "Token non valido: controlla la chiave di accesso ENTSO-E."
                  : `Il collegamento non risponde dalle ${fmtTime(status.error_since || status.last_error_at)}.`}
              </span>
            </div>
          )}

          {overviewFailed && !hasData ? (
            <div className="bg-surface border border-border rounded-[10px]">
              <LoadErrorBox what="i dati di mercato" onRetry={loadOverview} testId="energia-overview-error" />
            </div>
          ) : !hasData ? (
            <div className="bg-surface border border-border rounded-[10px] p-8 text-center" data-testid="energia-no-data">
              <div className="text-sm text-text-secondary mb-3">
                Nessun dato di mercato in archivio al momento.
              </div>
              {canRefresh && (
                <Button onClick={doRefresh} disabled={refreshing} className="bg-accent hover:bg-accent/90 text-white" data-testid="energia-no-data-refresh">
                  {refreshing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <RefreshCw size={14} className="mr-1.5" />}
                  Aggiorna adesso
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Riquadri zona */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-6" data-testid="zone-cards">
                {(overview?.zones || []).map((z) => <ZoneCard key={z.code} zone={z} />)}
              </div>

              {/* Selettore intervallo + legenda zone */}
              <div className="bg-surface border border-border rounded-[10px] p-4 mb-5" data-testid="energia-chart-card">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                  <div className="flex gap-1.5 flex-wrap">
                    {RANGE_OPTIONS.map((opt) => {
                      const isTomorrow = opt.key === "tomorrow";
                      const disabled = isTomorrow && !overview?.available_tomorrow;
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          disabled={disabled}
                          onClick={() => changeRange(opt.key)}
                          data-testid={`range-${opt.key}`}
                          className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                            range === opt.key
                              ? "bg-text-primary text-white border-text-primary"
                              : disabled
                                ? "bg-subtle border-border text-text-muted cursor-not-allowed opacity-60"
                                : "bg-subtle border-border text-text-secondary hover:border-text-primary"
                          }`}
                        >
                          {opt.label}
                          {disabled && <span className="ml-1 font-normal">· disponibili dalle 13</span>}
                        </button>
                      );
                    })}
                  </div>
                  <Button
                    onClick={doExport}
                    disabled={exporting || rows.length === 0}
                    variant="outline"
                    size="sm"
                    data-testid="energia-export-btn"
                  >
                    {exporting ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Download size={13} className="mr-1.5" />}
                    Esporta
                  </Button>
                </div>

                <div className="flex gap-1.5 flex-wrap mb-3" data-testid="zone-legend">
                  {zones.map((z) => {
                    const on = !!visible[z.code];
                    return (
                      <button
                        key={z.code}
                        type="button"
                        onClick={() => setVisible((v) => ({ ...v, [z.code]: !v[z.code] }))}
                        data-testid={`legend-${z.code}`}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${
                          on ? "border-text-primary text-text-primary bg-[#F4F430]/40" : "border-border text-text-muted bg-subtle"
                        }`}
                      >
                        <svg width="16" height="6" className="shrink-0" aria-hidden="true">
                          <line x1="0" y1="3" x2="16" y2="3" stroke={ZONE_COLORS[z.code]} strokeWidth="2"
                                strokeDasharray={ZONE_DASH[z.code] || undefined} opacity={on ? 1 : 0.35} />
                        </svg>
                        {z.label}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[11px] text-text-muted mb-3" data-testid="zone-overlap-note">
                  A ogni zona il suo tratteggio: quando i prezzi coincidono (nessuna congestione di rete) le linee si
                  sovrappongono — il dettaglio nel riquadro al passaggio del mouse elenca tutte le zone con lo stesso valore.
                </div>

                {loadingSerie ? (
                  <div className="h-72 flex items-center justify-center text-text-muted">
                    <Loader2 size={18} className="animate-spin" />
                  </div>
                ) : serieFailed ? (
                  <div className="h-72 flex items-center justify-center">
                    <LoadErrorBox compact what="la serie dei prezzi" onRetry={() => loadSerie(range)} testId="energia-serie-error" />
                  </div>
                ) : rows.length === 0 ? (
                  <div className="h-72 flex items-center justify-center text-sm text-text-muted" data-testid="energia-serie-empty">
                    Nessun dato per l'intervallo selezionato.
                  </div>
                ) : (
                  <div className="h-72" data-testid="energia-line-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                        <CartesianGrid stroke="#E8E8E6" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B7280" }} tickMargin={6} interval={tickStep - 1} />
                        <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} width={48}
                               domain={[(dataMin) => Math.min(0, dataMin), "auto"]}
                               label={{ value: "€/MWh", angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#9CA3AF" } }} />
                        <Tooltip content={<ChartTooltip zones={zones} />} />
                        {zones.filter((z) => visible[z.code]).map((z) => (
                          <Line
                            key={z.code}
                            type="monotone"
                            dataKey={z.code}
                            stroke={ZONE_COLORS[z.code]}
                            strokeWidth={1.8}
                            strokeDasharray={ZONE_DASH[z.code] || undefined}
                            dot={false}
                            connectNulls
                            name={z.code}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Tabella */}
              {rows.length > 0 && (
                <div className="bg-surface border border-border rounded-[10px] overflow-hidden mb-5" data-testid="energia-table-card">
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12.5px]" data-testid="energia-price-table">
                      <thead>
                        <tr className="border-b border-border bg-subtle/60">
                          <th className="text-left px-3 py-2 font-semibold text-text-secondary">
                            {serie?.granularity === "hour" ? "Ora" : "Giorno"}
                          </th>
                          {zones.map((z) => (
                            <th key={z.code} className="text-right px-3 py-2 font-semibold text-text-secondary whitespace-nowrap">{z.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => (
                          <tr key={row.key} className="border-b border-border/60 last:border-0">
                            <td className="px-3 py-1.5 text-text-secondary whitespace-nowrap">{row.label}</td>
                            {zones.map((z) => (
                              <td key={z.code} className="px-3 py-1.5 text-right tabular-nums text-text-primary">{fmt2(row[z.code])}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-border bg-subtle/60 font-semibold">
                          <td className="px-3 py-2 text-text-primary">Media</td>
                          {zones.map((z) => (
                            <td key={z.code} className="px-3 py-2 text-right tabular-nums text-text-primary" data-testid={`table-avg-${z.code}`}>
                              {fmt2(averages[z.code])}
                            </td>
                          ))}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <div className="px-3 py-2 border-t border-border text-[11px] text-text-muted" data-testid="energia-zero-note">
                    I prezzi a <b>0,00 €/MWh</b> sono reali: capitano nelle ore di sovrapproduzione fotovoltaica,
                    quando l'offerta di energia supera la domanda. Il trattino (<b>—</b>) indica invece un dato
                    non ancora pubblicato dalla fonte.
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      <div className="mt-6 text-[11px] text-text-muted" data-testid="energia-source-footer">
        Fonte: ENTSO-E Transparency Platform. Ultimo aggiornamento: {fmtDateTime(overview?.last_update)}.
      </div>
    </div>
  );
}
